/** @jsxRuntime classic */
/** @jsx jsx */
import { Subscription, WebsiteAppKind } from 'src/orchd-client';
import { jsx } from '@emotion/core';

import { SelectLabelWithIcon } from 'src/components/SelectLabelWithIcon/SelectLabelWithIcon';
import { IconName } from 'src/components/Icon/Icon.types';

import { WOOCOMMERCE } from 'src/utils/constants';
import { PackageAllowanceName, PackageSelectionName } from 'src/utils/packages/types';
import { getSubscriptionAllowance, getSubscriptionSelection } from 'src/utils/packages/utils';

/** The "start from scratch" choice, which is not an application at all. */
export const NEW_WEBSITE_BLANK = 'blank';

/** Everything the customer can pick on the "new website" tab. */
export type NewWebsiteAppChoice = typeof NEW_WEBSITE_BLANK | WebsiteAppKind | typeof WOOCOMMERCE;

/**
 * An application the customer can pick: the blank site is not one, so the package's
 * pre-installed application and every application card are of this type.
 */
export type NewWebsiteAppOption = Exclude<NewWebsiteAppChoice, typeof NEW_WEBSITE_BLANK>;

/**
 * Filter category of an application shown in the app selection. Only real
 * applications are mapped: a blank site belongs to no category, so it is offered
 * through the "All" filter only.
 */
export type NewWebsiteAppCategory = 'apps' | 'ai';

/**
 * Where an application is categorised. Anything added to `WebsiteAppKind` (or
 * offered as a pseudo application like WooCommerce) has to be added here as well:
 * the app selection builds both its filters and its cards from these two maps.
 */
export const WEBSITE_APP_CATEGORY: Record<
  Exclude<NewWebsiteAppChoice, typeof NEW_WEBSITE_BLANK>,
  NewWebsiteAppCategory
> = {
  [WebsiteAppKind.wordpress]: 'apps',
  [WebsiteAppKind.joomla]: 'apps',
  [WOOCOMMERCE]: 'apps',
  [WebsiteAppKind.openclaw]: 'ai',
  [WebsiteAppKind.n8n]: 'ai',
};

/** The applications offered in the app selection, in card order. */
export const NEW_WEBSITE_APPS: NewWebsiteAppChoice[] = [
  WebsiteAppKind.wordpress,
  WOOCOMMERCE,
  WebsiteAppKind.joomla,
  WebsiteAppKind.openclaw,
  WebsiteAppKind.n8n,
];

/**
 * The offered applications without the blank site: the package's pre-installed
 * selection and the form's application field are matched against this list, so an
 * unrecognised value can never become a card value no card renders.
 */
const NEW_WEBSITE_APP_OPTIONS: NewWebsiteAppOption[] = NEW_WEBSITE_APPS.filter(
  (choice): choice is NewWebsiteAppOption => choice !== NEW_WEBSITE_BLANK
);

/**
 * The `app` value orchd expects for each card of the app selection. WooCommerce
 * is a client-side pseudo application: it installs WordPress and differs only in
 * the plugins `getPreInstalledPlugins()` puts on it, so it folds onto that kind.
 * A "blank" website installs nothing at all and therefore has no kind; the
 * "start from scratch" branch never reaches this code path.
 *
 * Typed as a Record over the choices on purpose, like WEBSITE_APP_CATEGORY
 * below: an application added to WebsiteAppKind fails to compile here until it
 * is listed, instead of quietly installing WordPress as the default branch did.
 */
const WEBSITE_APP_KIND: Record<Exclude<NewWebsiteAppChoice, typeof NEW_WEBSITE_BLANK>, WebsiteAppKind> = {
  [WebsiteAppKind.wordpress]: WebsiteAppKind.wordpress,
  [WOOCOMMERCE]: WebsiteAppKind.wordpress,
  [WebsiteAppKind.joomla]: WebsiteAppKind.joomla,
  [WebsiteAppKind.openclaw]: WebsiteAppKind.openclaw,
  [WebsiteAppKind.n8n]: WebsiteAppKind.n8n,
};

export const getWebsiteAppKindForChoice = (choice: NewWebsiteAppChoice): WebsiteAppKind =>
  choice === NEW_WEBSITE_BLANK ? undefined : WEBSITE_APP_KIND[choice];

/**
 * Whether a value stored in the form's application field is one of the applications
 * actually offered: an empty (blank site) or unrecognised value brands nothing.
 */
export const isWebsiteAppOption = (value?: string | number): value is NewWebsiteAppOption =>
  typeof value === 'string' && NEW_WEBSITE_APP_OPTIONS.includes(value as NewWebsiteAppOption);

/** Whether an application belongs to a filter category. */
export const isWebsiteAppCategory = (app: NewWebsiteAppChoice, category: NewWebsiteAppCategory): boolean =>
  app !== NEW_WEBSITE_BLANK && WEBSITE_APP_CATEGORY[app] === category;

/**
 * The application the selected package pre-installs, undefined for a plain package.
 *
 * The package stores the selection as free text with inconsistent casing -
 * `WordPress`, `WooCommerce`, `joomla`, `openclaw`, `n8n`, `None` - so it is
 * matched case-insensitively against the applications actually offered; anything
 * unrecognised counts as no pre-installed application rather than becoming a
 * card value no card renders.
 */
export const getPackagePreinstalledApp = (subscription?: Subscription): NewWebsiteAppOption | undefined => {
  const app = getSubscriptionSelection(subscription, PackageSelectionName.applicationsPreinstalledApp)?.value;

  if (!app || app === 'None') return undefined;

  return NEW_WEBSITE_APP_OPTIONS.find((choice) => choice.toLowerCase() === app.toLowerCase());
};

/**
 * Whether the customer may install applications by hand on top of what the
 * package pre-installs.
 */
export const isSoftwareInstallerAllowed = (subscription?: Subscription, isMasterOrg = false): boolean =>
  isMasterOrg || !!getSubscriptionAllowance(subscription, PackageAllowanceName.applicationsSoftwareInstaller);

/**
 * Filter the applications by what the subscription offers, keeping card order. A
 * blank site is always offered; AI agents follow the persistent apps setting of the
 * subscription; everything else follows the applications allowed by the package
 * which, when installing by hand is not allowed, leaves the package's own
 * application as the only choice.
 */
export const filterAppChoices = (
  choices: NewWebsiteAppChoice[],
  subscription?: Subscription,
  isMasterOrg = false
): NewWebsiteAppChoice[] => {
  const preinstalled = getPackagePreinstalledApp(subscription);
  const allowed = subscription?.allowedApps?.map((app: string) => app.toLowerCase());
  const installerAllowed = isSoftwareInstallerAllowed(subscription, isMasterOrg);

  return choices.filter((choice: NewWebsiteAppChoice) => {
    if (choice === NEW_WEBSITE_BLANK) return true;

    const id = choice.toLowerCase();

    // AI agents skip the software-installer allowance but not an explicit
    // allowed-apps list: orchd's check_app_allowed_by_subscription filters
    // openclaw/n8n through the plan's allowed_apps too, and only skips the
    // Node.js (persistent apps) requirement for a master org.
    if (isWebsiteAppCategory(choice, 'ai'))
      return (
        (isMasterOrg || !!subscription?.persistentAppsAllowed) &&
        (!allowed || allowed.length === 0 || allowed.includes(id))
      );

    if (!installerAllowed) return preinstalled !== undefined && id === preinstalled;

    return !allowed || allowed.length === 0 || allowed.includes(id);
  });
};

const APP_OPTION_LABELS: Record<WebsiteAppKind | typeof WOOCOMMERCE, string> = {
  [WebsiteAppKind.wordpress]: 'WordPress',
  [WebsiteAppKind.joomla]: 'Joomla',
  [WebsiteAppKind.openclaw]: 'OpenClaw',
  [WebsiteAppKind.n8n]: 'n8n',
  [WOOCOMMERCE]: 'WooCommerce',
};

const APP_OPTION_ICONS: Record<WebsiteAppKind | typeof WOOCOMMERCE, IconName> = {
  [WebsiteAppKind.wordpress]: 'wordpress',
  [WebsiteAppKind.joomla]: 'joomla',
  [WebsiteAppKind.openclaw]: 'openclaw',
  [WebsiteAppKind.n8n]: 'n8n',
  [WOOCOMMERCE]: 'woocommerce',
};

/**
 * Display name and icon list entry of an application. The app selection, its
 * dropdown and the "new website" tab card all brand an application from these, so
 * the three never disagree.
 */
export const getAppOptionLabel = (app: NewWebsiteAppOption): string => APP_OPTION_LABELS[app];

export const getAppOptionIcon = (app: NewWebsiteAppOption): IconName => APP_OPTION_ICONS[app];

export const renderAppSelectOption = (appKind: WebsiteAppKind | typeof WOOCOMMERCE) => {
  return {
    value: appKind,
    label: (
      <SelectLabelWithIcon iconSize="xl" icon={APP_OPTION_ICONS[appKind]}>
        {APP_OPTION_LABELS[appKind]}
      </SelectLabelWithIcon>
    ),
  };
};
